import React from 'react';

interface BrandLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  variant?: 'full' | 'icon';
}

const sizeMap = {
  sm: { icon: 36, text: 24, height: 44 },
  md: { icon: 48, text: 32, height: 56 },
  lg: { icon: 72, text: 48, height: 80 },
  xl: { icon: 96, text: 64, height: 104 },
};

export const BrandLogo: React.FC<BrandLogoProps> = ({
  className = '',
  size = 'md',
  showText = true,
  variant = 'full',
}) => {
  const dimensions = sizeMap[size];
  const iconOnly = variant === 'icon' || !showText;
  
  // Calculate proper width based on content (icon + text width)
  const iconWidth = 70;
  const textWidth = iconOnly ? 0 : 240; // Approximate width of "MomentumEQ" text
  const viewBoxWidth = iconOnly ? iconWidth : iconWidth + textWidth;
  
  // Scale the rendered width proportionally to the height
  const scale = dimensions.height / 70;
  const width = viewBoxWidth * scale;
  const height = dimensions.height;
  
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${viewBoxWidth} 70`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="MomentumEQ Logo"
    >
      <defs>
        <linearGradient id="brand-gradient" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="hsl(277, 90%, 65%)" />
          <stop offset="100%" stopColor="hsl(173, 80%, 40%)" />
        </linearGradient>
      </defs>
      
      {/* Mountain peaks with gradient fill */}
      <path
        d="M5 60 L25 25 L35 42 L52 12 L70 60 Z"
        fill="url(#brand-gradient)"
        opacity="0.25"
      />
      
      {/* Mountain peaks outline */}
      <path
        d="M5 60 L25 25 L35 42 L52 12 L70 60"
        stroke="url(#brand-gradient)"
        strokeWidth="3"
        fill="none"
        strokeLinejoin="round"
      />
      
      {/* Summit marker */}
      <circle cx="52" cy="12" r="5" fill="hsl(173, 80%, 40%)" />
      <circle
        cx="52"
        cy="12"
        r="8"
        stroke="hsl(173, 80%, 40%)"
        strokeWidth="2"
        fill="none"
        opacity="0.4"
      />
      
      {/* Wordmark */}
      {!iconOnly && (
        <text
          x="88"
          y="42"
          dominantBaseline="middle"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          fontSize={dimensions.text}
          fontWeight="700"
          fill="url(#brand-gradient)"
        >
          MomentumEQ
        </text>
      )}
    </svg>
  );
};

export const BrandIcon: React.FC<{ className?: string; size?: number }> = ({
  className = '',
  size = 32,
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 70 70"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="MomentumEQ Icon"
    >
      <defs>
        <linearGradient id="brand-icon-gradient" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="hsl(277, 90%, 65%)" />
          <stop offset="100%" stopColor="hsl(173, 80%, 40%)" />
        </linearGradient>
      </defs>
      
      {/* Mountain peaks with gradient fill */}
      <path
        d="M5 60 L25 25 L35 42 L52 12 L70 60 Z"
        fill="url(#brand-icon-gradient)"
        opacity="0.25"
      />
      
      {/* Mountain peaks outline */}
      <path
        d="M5 60 L25 25 L35 42 L52 12 L70 60"
        stroke="url(#brand-icon-gradient)"
        strokeWidth="3"
        fill="none"
        strokeLinejoin="round"
      />
      
      {/* Summit marker */}
      <circle cx="52" cy="12" r="5" fill="hsl(173, 80%, 40%)" />
    </svg>
  );
};

export default BrandLogo;
